import Image from "next/image";
import React, { useState, useEffect } from "react";
import styles from "./AlumniSuccessStory.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { MoveLeft, MoveRight, Play } from "lucide-react";

interface SuccessStoryData {
  id: number;
  title: string;
  cards: Card[];
}

interface Card {
  id: number;
  title_description_image: {
    id: number;
    title_description: {
      id: number;
      title: string;
      description: string;
    };
    images: Image | null;
    mobiles_images: Image | null;
  };
  link_with_image: {
    id: number;
    link: {
      id: number;
      label: string;
      url: string;
    };
    image: Image;
    mobile_image: Image | null;
  };
}

interface Image {
  alternativeText: string | null;
  url: string;
}

interface Props {
  data: SuccessStoryData;
  page?: string;
}

const AlumniSuccessStory: React.FC<Props> = ({ data, page }) => {
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);


  useEffect(() => {
    // Function to check if the screen is mobile
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Reset playing video when slide changes
  useEffect(() => {
    // Only keep a video playing if we're on its slide
    if (data?.cards && activeSlide < data.cards.length) {
      const currentCardId = data.cards[activeSlide].id;
      if (playingVideoId !== null && playingVideoId !== currentCardId) {
        setPlayingVideoId(null);
      }
    }
  }, [activeSlide, data?.cards, playingVideoId]);

  // Function to handle playing a video
  const handlePlayVideo = (cardId: number) => {
    setPlayingVideoId(cardId);
  };

  const extractYouTubeID = (url: string): string | null => {
    const match = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );
    return match ? match[1] : null;
  };

  return (
    <section
      className={`${styles.nextContainer} ${
        page === "alumni" ? styles.alumniContainer : ""
      }`}
      id={page === "alumni" ? "AlumniSuccesStories" : undefined}
    >
      <div className={`${styles.topContainer} container`}>
        <h2
          className={`${styles.title} ${
            page === "alumni" ? styles.alumniTitle : ""
          }`}
        >
          {data?.title}
        </h2>
        {data?.cards && data.cards.length > 1 && (
          <div className="d-none d-md-block">
            <div className="d-flex">
              <button
                className={`${styles.customArrow} ${styles.prevButton} ${
                  page === "alumni" ? styles.alumniArrow : ""
                }`}
                aria-label="Previous Slide"
                style={{ marginRight: "10px" }}
              >
                <MoveLeft />
              </button>
              <button
                className={`${styles.customArrow} ${styles.nextButton} ${
                  page === "alumni" ? styles.alumniArrow : ""
                }`}
                aria-label="Next Slide"
              >
                <MoveRight />
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="container">
        <Swiper
          spaceBetween={30}
          slidesPerView={1}
          navigation={
            data?.cards && data.cards.length > 1
              ? {
                  nextEl: `.${styles.nextButton}`,
                  prevEl: `.${styles.prevButton}`,
                }
              : false
          }
          modules={[Navigation]}
          loop={data?.cards && data.cards.length > 1}
          onSlideChange={(swiper) => {
            // Update the active slide
            setActiveSlide(swiper.realIndex);
          }}
        >
          {data?.cards?.map((card, index) => {
            const videoId = extractYouTubeID(card?.link_with_image?.link?.url);
            // A video is playing only if it's the current playing video ID and we're on its slide
            const isPlaying =
              playingVideoId === card?.id && activeSlide === index;
            return (
              <SwiperSlide key={card?.id}>
                <div className={styles.missionContent}>
                  <div className={styles.videoCard}>
                    {!isPlaying ? (
                      <div className={styles.cover}>
                        <div className={styles.overlay}></div>
                        <Image
                          src={
                            isMobile && card?.link_with_image?.mobile_image
                              ? card.link_with_image.mobile_image.url
                              : card?.link_with_image?.image?.url
                          }
                          alt={
                            (isMobile &&
                              card?.link_with_image?.mobile_image
                                ?.alternativeText) ||
                            card?.link_with_image?.image?.alternativeText ||
                            "Video thumbnail"
                          }
                          title={
                            (isMobile &&
                              card?.link_with_image?.mobile_image
                                ?.alternativeText) ||
                            card?.link_with_image?.image?.alternativeText ||
                            "Video thumbnail"
                          }
                          fill
                          style={{
                            objectFit: "cover",
                          }}
                          className={styles.thumbnail}
                        />
                        <button
                          aria-label="Play Button"
                          className={styles.playButton}
                          onClick={() => handlePlayVideo(card?.id)}
                        >
                          <Play size={isMobile ? 24 : 30} fill="currentColor" />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.videoBanner}>
                        <iframe
                          className={styles.youtubeVideo}
                          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&modestbranding=1&controls=1&rel=0&showinfo=0&iv_load_policy=3&fs=1&disablekb=1`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin"
                          key={`video-${card.id}-${playingVideoId}`} // Force iframe to re-render when playingVideoId changes
                        ></iframe>
                      </div>
                    )}
                  </div>
                  <div
                    className={`${styles.textContent} ${
                      page === "alumni" ? styles.alumniTextContent : ""
                    }`}
                  >
                    <h3
                      className={page === "alumni" ? styles.alumniHeading : ""}
                    >
                      {card?.title_description_image?.title_description?.title}
                    </h3>
                    <p
                      className={
                        page === "alumni" ? styles.alumniParagraph : ""
                      }
                    >
                      {
                        card?.title_description_image?.title_description
                          ?.description
                      }
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {data?.cards && data.cards.length > 1 && (
        <div className="d-md-none d-block">
          <div className="d-flex align-items-center justify-content-center">
            <button
              className={`${styles.customArrow} ${styles.prevButton} ${
                page === "alumni" ? styles.alumniArrow : ""
              }`}
              aria-label="Previous Slide"
              style={{ marginRight: "10px" }}
            >
              <MoveLeft />
            </button>
            <button
              className={`${styles.customArrow} ${styles.nextButton} ${
                page === "alumni" ? styles.alumniArrow : ""
              }`}
              aria-label="Next Slide"
            >
              <MoveRight />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default AlumniSuccessStory;
